import { useEffect, useState } from 'react';
import { Stack, Group, Text, Button, Badge, Card, SimpleGrid, Anchor } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { NumberInput, Textarea, TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { TenantName } from '../../../../components/TenantName';
import { useTenantMap } from '../../../../data/useTenants';
import { formatMinor, formatDateTime, formatDate, formatInteger } from '../../../../lib/format';
import type { Indexed } from '../../../../lib/types';
import {
  useAffiliate, useAffiliateAction, useUpdateAffiliate,
  useAttachReferral, useRevokeReferral, useAffiliateAdjustment, useRunAccrual
} from '../../../../data/useAffiliates';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * One partner, and every decision an operator can take about them.
 *
 * The four state actions ask for a reason and say what will happen, because
 * each of them changes what somebody gets paid. The earnings table shows the
 * basis each share was worked out from, which is the only place that figure
 * appears: it is what the platform kept, and the partner is never shown it.
 */
export function AffiliateDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const t = useT();
  const detail = useAffiliate(id);
  const action = useAffiliateAction();
  const update = useUpdateAffiliate();
  const attach = useAttachReferral();
  const revoke = useRevokeReferral();
  const adjust = useAffiliateAdjustment();
  const accrual = useRunAccrual();
  const { map } = useTenantMap();

  const [percent, setPercent] = useState<number | ''>('');
  const [months, setMonths] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [attachOrg, setAttachOrg] = useState('');
  const [attachReason, setAttachReason] = useState('');
  const [adjustAmount, setAdjustAmount] = useState<number | ''>('');
  const [adjustReason, setAdjustReason] = useState('');

  const d = detail.data?.data;
  const a = d?.affiliate;

  useEffect(() => {
    if (!a) return;
    setPercent(a.commission_percent);
    setMonths(a.commission_months ?? '');
    setNotes(a.admin_notes ?? '');
    setAttachOrg('');
    setAttachReason('');
    setAdjustAmount('');
    setAdjustReason('');
  }, [a?.id]);

  function run(label: string, act: 'approve' | 'suspend' | 'reactivate' | 'close', needsReason: boolean) {
    if (!id) return;
    let reason = '';
    openConfirmModal({
      title: label,
      children: (
        <Stack gap="sm">
          <Text role="bodySmall">{t(`partners.action.${act}Body`, { name: a?.display_name ?? '' })}</Text>
          {needsReason && (
            <Textarea
              label={t('partners.action.reason')}
              description={t('partners.action.reasonHint')}
              onChange={(e) => { reason = e.currentTarget.value; }}
              minRows={2}
            />
          )}
        </Stack>
      ),
      labels: { confirm: label, cancel: t('action.cancel') },
      onConfirm: async () => {
        if (needsReason && !reason.trim()) { notifyError(t('partners.action.reasonRequired')); return; }
        try {
          await action.mutateAsync({ id, action: act, reason: reason.trim() || undefined });
          notifySuccess(t('partners.action.done'));
        } catch (e) {
          notifyError(e instanceof Error ? e.message : t('partners.action.failed'));
        }
      }
    });
  }

  const tenantOptions = [...map.entries()].map(([orgId, tenant]) => ({
    value: orgId,
    label: (tenant as { name?: string }).name ?? orgId
  }));

  return (
    <Drawer opened={Boolean(id)} onClose={onClose} position="right" size="xl" title={a?.display_name ?? t('partners.title')}>
      {detail.isLoading ? (
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>
      ) : detail.isError || !d || !a ? (
        <Alert color="error" role="alert">
          {t('partners.loadFailed')}{' '}
          <Button variant="subtle" size="sm" onClick={() => void detail.refetch()}>{t('action.retry')}</Button>
        </Alert>
      ) : (
        <Stack gap="lg">
          {/* Who they are */}
          <Card withBorder>
            <Stack gap="sm">
              <Group justify="space-between">
                <Badge color={a.status === 'active' ? 'success' : a.status === 'pending_review' ? 'warning' : a.status === 'suspended' ? 'error' : undefined}>
                  {t(`partners.status.${a.status}`, a.status)}
                </Badge>
                <Text role="caption" c="var(--rr-color-text-muted)">{a.code ?? t('partners.noCode')}</Text>
              </Group>
              {a.status_reason && <Alert color="warning">{a.status_reason}</Alert>}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <Field label={t('partners.field.account')}><TenantName id={a.organisation_id} withCopy /></Field>
                <Field label={t('partners.field.email')}>{a.contact_email}</Field>
                <Field label={t('partners.field.business')}>{a.business_name ?? '—'}</Field>
                <Field label={t('partners.field.phone')}>{a.contact_phone ?? '—'}</Field>
                <Field label={t('partners.field.website')}>
                  {a.website ? <Anchor href={a.website} target="_blank" rel="noreferrer">{a.website}</Anchor> : '—'}
                </Field>
                <Field label={t('partners.field.applied')}>{formatDateTime(a.applied_at)}</Field>
              </SimpleGrid>
              {a.how_you_promote && (
                <Stack gap={4}>
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('partners.field.how')}</Text>
                  <Text role="bodySmall">{a.how_you_promote}</Text>
                </Stack>
              )}
              <Group gap="sm">
                {(a.status === 'pending_review' || a.status === 'suspended') && (
                  <Button size="sm" onClick={() => run(t('partners.action.approve'), a.status === 'suspended' ? 'reactivate' : 'approve', false)}>
                    {a.status === 'suspended' ? t('partners.action.reactivate') : t('partners.action.approve')}
                  </Button>
                )}
                {a.status === 'active' && (
                  <Button size="sm" variant="light" onClick={() => run(t('partners.action.suspend'), 'suspend', true)}>
                    {t('partners.action.suspend')}
                  </Button>
                )}
                {a.status !== 'closed' && (
                  <Button size="sm" variant="subtle" onClick={() => run(t('partners.action.close'), 'close', true)}>
                    {t('partners.action.close')}
                  </Button>
                )}
                <Button size="sm" variant="subtle" loading={accrual.isPending} onClick={() => void accrual.mutateAsync(a.id)}>
                  {t('partners.accrual.runOne')}
                </Button>
              </Group>
            </Stack>
          </Card>

          {/* What they are owed */}
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            <Field label={t('partners.column.pending')}>{formatMinor(a.pending_minor, 'ZAR')}</Field>
            <Field label={t('partners.column.available')}>{formatMinor(a.available_minor, 'ZAR')}</Field>
            <Field label={t('partners.field.lifetime')}>{formatMinor(a.lifetime_minor, 'ZAR')}</Field>
            <Field label={t('partners.field.withdrawn')}>{formatMinor(a.withdrawn_minor, 'ZAR')}</Field>
          </SimpleGrid>

          {/* The rate */}
          <Card withBorder>
            <Stack gap="sm">
              <Text role="label">{t('partners.rate.title')}</Text>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('partners.rate.hint')}</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <NumberInput label={t('partners.rate.percent')} value={percent} onChange={(v) => setPercent(typeof v === 'number' ? v : '')} min={0} max={100} step={1} />
                <NumberInput label={t('partners.rate.months')} description={t('partners.rate.monthsHint')} value={months} onChange={(v) => setMonths(typeof v === 'number' ? v : '')} min={1} max={120} />
              </SimpleGrid>
              <Textarea label={t('partners.rate.notes')} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} minRows={2} />
              <Group>
                <Button
                  size="sm"
                  loading={update.isPending}
                  onClick={async () => {
                    try {
                      await update.mutateAsync({
                        id: a.id,
                        commission_percent: percent === '' ? undefined : percent,
                        commission_months: months === '' ? null : months,
                        admin_notes: notes,
                        reason: t('partners.rate.reason')
                      });
                      notifySuccess(t('partners.rate.saved'));
                    } catch { notifyError(t('partners.rate.failed')); }
                  }}
                >
                  {t('action.save')}
                </Button>
              </Group>
            </Stack>
          </Card>

          {/* Their referrals */}
          <Card withBorder>
            <Stack gap="sm">
              <Text role="label">{t('partners.referrals.title')}</Text>
              {d.referrals.length === 0 ? (
                <EmptyState title={t('partners.referrals.empty')} description={t('partners.referrals.emptyHint')} />
              ) : (
                <DataTable<Indexed<(typeof d.referrals)[number]>>
                  idAccessor="id"
                  records={d.referrals}
                  columns={[
                    { accessor: 'referred_organisation_id', title: t('partners.column.company'), render: (r) => <TenantName id={r.referred_organisation_id} /> },
                    { accessor: 'source', title: t('partners.column.source'), render: (r) => t(`partners.source.${r.source}`, r.source) },
                    { accessor: 'attributed_at', title: t('partners.column.since'), render: (r) => formatDate(r.attributed_at) },
                    { accessor: 'commission_percent', title: t('partners.column.share'), render: (r) => `${r.commission_percent}%` },
                    { accessor: 'earned_minor', title: t('partners.column.earned'), render: (r) => formatMinor(r.earned_minor, 'ZAR') },
                    {
                      accessor: 'flags',
                      title: t('partners.column.flags'),
                      render: (r) => r.flags.length === 0
                        ? <Text role="caption" c="var(--rr-color-text-muted)">{t('partners.flags.none')}</Text>
                        : <Group gap={4}>{r.flags.map((f) => <Badge key={f} color="warning">{t(`partners.flag.${f}`, f)}</Badge>)}</Group>
                    },
                    {
                      accessor: 'status',
                      title: t('partners.column.status'),
                      render: (r) => r.status === 'revoked'
                        ? <Badge color="error">{t('partners.status.revoked')}</Badge>
                        : (
                          <Button
                            variant="subtle"
                            size="sm"
                            onClick={() => {
                              let reason = '';
                              openConfirmModal({
                                title: t('partners.referrals.revokeTitle'),
                                children: (
                                  <Stack gap="sm">
                                    <Text role="bodySmall">{t('partners.referrals.revokeBody')}</Text>
                                    <Textarea label={t('partners.action.reason')} onChange={(e) => { reason = e.currentTarget.value; }} minRows={2} />
                                  </Stack>
                                ),
                                labels: { confirm: t('partners.referrals.revokeConfirm'), cancel: t('action.cancel') },
                                onConfirm: async () => {
                                  if (!reason.trim()) { notifyError(t('partners.action.reasonRequired')); return; }
                                  try {
                                    await revoke.mutateAsync({ referralId: r.id, reason: reason.trim() });
                                    notifySuccess(t('partners.referrals.revoked'));
                                  } catch { notifyError(t('partners.referrals.revokeFailed')); }
                                }
                              });
                            }}
                          >
                            {t('partners.referrals.revoke')}
                          </Button>
                        )
                    }
                  ]}
                />
              )}

              <Stack gap="sm">
                <Text role="caption" c="var(--rr-color-text-muted)">{t('partners.referrals.attachHint')}</Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <Select
                    label={t('partners.referrals.attachCompany')}
                    value={attachOrg}
                    onChange={(e) => setAttachOrg(e.currentTarget.value)}
                    data={[{ value: '', label: t('partners.referrals.attachPick') }, ...tenantOptions]}
                  />
                  <TextInput label={t('partners.action.reason')} value={attachReason} onChange={(e) => setAttachReason(e.currentTarget.value)} />
                </SimpleGrid>
                <Group>
                  <Button
                    size="sm"
                    variant="light"
                    disabled={!attachOrg || !attachReason.trim()}
                    loading={attach.isPending}
                    onClick={async () => {
                      try {
                        await attach.mutateAsync({ id: a.id, organisation_id: attachOrg, reason: attachReason.trim() });
                        notifySuccess(t('partners.referrals.attached'));
                        setAttachOrg('');
                        setAttachReason('');
                      } catch (e) {
                        notifyError(e instanceof Error ? e.message : t('partners.referrals.attachFailed'));
                      }
                    }}
                  >
                    {t('partners.referrals.attach')}
                  </Button>
                </Group>
              </Stack>
            </Stack>
          </Card>

          {/* The working behind the money */}
          <Card withBorder>
            <Stack gap="sm">
              <Text role="label">{t('partners.earnings.title')}</Text>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('partners.earnings.hint')}</Text>
              {d.earnings.length === 0 ? (
                <EmptyState title={t('partners.earnings.empty')} />
              ) : (
                <DataTable<Indexed<(typeof d.earnings)[number]>>
                  idAccessor="id"
                  records={d.earnings}
                  columns={[
                    { accessor: 'usage_date', title: t('partners.column.day'), render: (r) => r.usage_date ? formatDate(r.usage_date) : formatDateTime(r.occurred_at) },
                    { accessor: 'kind', title: t('partners.column.kind'), render: (r) => t(`partners.kind.${r.kind}`, r.kind) },
                    { accessor: 'referred_name', title: t('partners.column.company'), render: (r) => r.referred_name ?? '—' },
                    { accessor: 'basis_minor', title: t('partners.column.basis'), render: (r) => r.basis_minor == null ? '—' : formatMinor(r.basis_minor, 'ZAR') },
                    { accessor: 'percent_applied', title: t('partners.column.share'), render: (r) => r.percent_applied == null ? '—' : `${r.percent_applied}%` },
                    { accessor: 'entries', title: t('partners.column.entries'), render: (r) => r.entries == null ? '—' : formatInteger(r.entries) },
                    { accessor: 'amount_minor', title: t('partners.column.amount'), render: (r) => formatMinor(r.amount_minor, 'ZAR') }
                  ]}
                />
              )}

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <NumberInput
                  label={t('partners.adjust.amount')}
                  description={t('partners.adjust.amountHint')}
                  value={adjustAmount}
                  onChange={(v) => setAdjustAmount(typeof v === 'number' ? v : '')}
                  step={1}
                />
                <TextInput label={t('partners.action.reason')} value={adjustReason} onChange={(e) => setAdjustReason(e.currentTarget.value)} />
              </SimpleGrid>
              <Group>
                <Button
                  size="sm"
                  variant="light"
                  disabled={adjustAmount === '' || adjustAmount === 0 || !adjustReason.trim()}
                  loading={adjust.isPending}
                  onClick={async () => {
                    try {
                      await adjust.mutateAsync({
                        id: a.id,
                        amount_minor: Math.round(Number(adjustAmount) * 100),
                        reason: adjustReason.trim()
                      });
                      notifySuccess(t('partners.adjust.done'));
                      setAdjustAmount('');
                      setAdjustReason('');
                    } catch { notifyError(t('partners.adjust.failed')); }
                  }}
                >
                  {t('partners.adjust.action')}
                </Button>
              </Group>
            </Stack>
          </Card>

          {/* Who did what */}
          <Card withBorder>
            <Stack gap="sm">
              <Text role="label">{t('partners.events.title')}</Text>
              {d.events.length === 0 ? (
                <EmptyState title={t('partners.events.empty')} />
              ) : (
                <Stack gap="xs">
                  {d.events.map((e) => (
                    <Group key={e.id} justify="space-between" align="flex-start">
                      <Stack gap={2}>
                        <Text role="bodySmall">{t(`partners.event.${e.action}`, e.action)}</Text>
                        {e.reason && <Text role="caption" c="var(--rr-color-text-muted)">{e.reason}</Text>}
                      </Stack>
                      <Stack gap={2} align="flex-end">
                        <Text role="caption" c="var(--rr-color-text-muted)">{formatDateTime(e.occurred_at)}</Text>
                        <Text role="caption" c="var(--rr-color-text-muted)">{e.actor_email ?? t(`partners.actorScope.${e.actor_scope}`, e.actor_scope)}</Text>
                      </Stack>
                    </Group>
                  ))}
                </Stack>
              )}
            </Stack>
          </Card>
        </Stack>
      )}
    </Drawer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack gap={2}>
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <Text role="bodySmall">{children}</Text>
    </Stack>
  );
}
